// ─────────────────────────────────────────────────────────────────────────
// LabHistoryEditDialog
//
// Self-contained editor for a previously-saved lab. Opens from the pencil
// icon on any row of the Lab History table, so a technician can walk
// back to a two-year-old result, correct a typo (or amend after a
// clinical review) and save it back in place.
//
// Why it's a dedicated dialog and not a jump to the MyPatients entry
// dialog:
//   • MyPatients only lists patients that are current in the search
//     results (today's worklist, mostly). A 2-year-old patient won't
//     always be there — the existing "Edit Results" button inside the
//     "View Results for Date" dialog silently no-ops in that case.
//   • This dialog goes straight through `loadLabResults` +
//     `saveLabResults` — the same endpoints result entry uses — with
//     no dependency on any list of patients being loaded upstream.
//
// Update semantics: `saveLabResults` UPSERTS on `trans_code`. When we
// echo every row's `trans_code` back to it, the backend UPDATES the
// stored row instead of inserting a new one. `test_by` is overwritten
// with whoever is editing today — audit of the original recorder is
// lost unless a future migration adds edited_by/edited_at columns.
// That tradeoff is called out in the confirmation banner below.
//
// Two shapes, one dialog. `/loadLabResults` reports the category's
// `lab_format`, and a lab the admin marked **narrative** (Blood Film
// Comment, Peripheral Blood Smear, …) is edited as what it actually is:
// a section title over a paragraph of free text, plus one clinical
// Impression for the whole lab. Flag / Unit / Reference Range have no
// meaning for a description and are not rendered — showing them was what
// made a narrative lab come back looking like a numeric panel.
//
// Duplicate collapsing: a saved lab could previously accumulate a second
// copy of every row (see migration 044). Narrative sections are grouped
// by name on load, so the editor shows one text area per section rather
// than the same section repeated; the redundant rows are deleted when the
// edit is saved, which repairs the stored record as a side effect.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton, Chip,
  FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Divider, Paper, Stack, Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  WarningAmber as WarningIcon,
  Refresh as RefreshIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';

import { loadLabResults, saveLabResults, loadLabCategories, deleteResultC } from '../BaseURL';

// Same URL builder LabHistory uses — `loadLabResults` is a bare URL,
// not a helper, so we assemble the query string here for the GET.
const buildLoadUrl = (patientId, testCategory, date, labNo) => {
  const enc = (v) => encodeURIComponent(v ?? '');
  return `${loadLabResults}?patientId=${enc(patientId)}&testCategory=${enc(testCategory)}&date=${enc(date)}&labNo=${enc(labNo)}`;
};

// Reference range for a parameter that has no stored row yet. The catalog
// keeps a separate range per patient group, so we pick the one matching the
// patient this lab belongs to and fall back through the others rather than
// showing nothing — an empty range on a numeric result is worse than an
// approximate one, and the field stays read-only either way (ranges are
// maintained in Setup Lab). Narrative sections have no ranges at all and
// simply come back empty.
const rangeForPatient = (param, patientRow) => {
  const gender = String(patientRow?.gender || '').trim().toLowerCase();
  const age = Number.parseInt(patientRow?.age, 10);
  const ordered = [];
  if (Number.isFinite(age) && age < 1) ordered.push(param.neonates);
  //  Children's ranges for UNDER 12 only; 12 years 0 days is an adult.
  if (Number.isFinite(age) && age < 12) ordered.push(param.children);
  if (gender.startsWith('f')) ordered.push(param.female, param.male);
  else if (gender.startsWith('m')) ordered.push(param.male, param.female);
  else ordered.push(param.male, param.female);
  ordered.push(param.children, param.neonates);
  return ordered.find((v) => v && String(v).trim() !== '') || '';
};

const FLAG_OPTIONS = [
  { value: 'normal',   label: 'Normal',   color: '#16a34a' },
  { value: 'high',     label: 'High',     color: '#dc2626' },
  { value: 'low',      label: 'Low',      color: '#d97706' },
  { value: 'critical', label: 'Critical', color: '#7c1d1d' },
  { value: 'positive', label: 'Positive', color: '#0369a1' },
  { value: 'negative', label: 'Negative', color: '#0f766e' },
  { value: '-',        label: '—',        color: '#6b7280' },
];

export default function LabHistoryEditDialog({
  open,
  onClose,
  row,          // { id, name, path_no, dat, test_by, cat, ... } from LabHistory
  currentUser,  // string — whoever's editing right now
  onSaved,      // () => void — parent refreshes the list
}) {
  const [loading,   setLoading]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [rows,      setRows]      = useState([]);           // editable parameter rows
  const [impression, setImpression] = useState('');
  const [generalComment, setGeneralComment] = useState('');
  // 'parameter' (numeric panel — Result/Flag/Unit/Range per row) or
  // 'narrative' (descriptive lab — one paragraph per section + a single
  // Impression). Reported by /loadLabResults from the catalog's lab_format
  // so the editor matches how the lab was actually entered and printed.
  const [labFormat, setLabFormat] = useState('parameter');
  // trans_codes of duplicate rows collapsed on load. Deleted after a
  // successful save so the stored record stops printing each section twice.
  const [staleCodes, setStaleCodes] = useState([]);
  // Interpretation / reference-table HTML — pulled from lab_cat for
  // the current category so the same block that prints under the
  // report also shows up here while editing. Category-level (shared
  // across every patient's HbA1C, RFT, etc.), so it's rendered read-
  // only with a hint that Setup Lab is where you change it.
  const [interpretationHtml, setInterpretationHtml] = useState('');

  // Format the row's date as YYYY-MM-DD for the API. The row's `dat`
  // may come in as an ISO string, a DATE, or a JS Date depending on
  // where in the codebase it originated.
  const isoDate = useMemo(() => {
    if (!row?.dat) return '';
    try {
      return new Date(row.dat).toISOString().slice(0, 10);
    } catch (_) { return ''; }
  }, [row?.dat]);

  const load = async () => {
    if (!row) return;
    setLoading(true);
    setError('');
    setInterpretationHtml('');
    setStaleCodes([]);
    try {
      // Fetch results + the category's interpretation block in parallel.
      // The interpretation is optional — if it fails or the category
      // has none, the rest of the dialog still works.
      const [resultsResp, categoriesResp] = await Promise.all([
        axios.get(buildLoadUrl(row.id, row.cat, isoDate, row.path_no), { timeout: 15000 }),
        axios.get(loadLabCategories, { timeout: 15000 }).catch(() => null),
      ]);
      const data = resultsResp.data;
      if (!data?.success) {
        setError(data?.message || 'Could not load the saved results.');
        setRows([]);
        return;
      }
      const loaded = Array.isArray(data.results) ? data.results : [];

      // Match by category name (case-insensitive so a "HBA1C" row
      // matches a "HbA1c" catalog entry). Same column the report
      // template joins against.
      if (categoriesResp?.data?.success && Array.isArray(categoriesResp.data.categories)) {
        const wanted = String(row.cat || '').trim().toLowerCase();
        const match = categoriesResp.data.categories.find(
          (c) => String(c.cat || '').trim().toLowerCase() === wanted
        );
        setInterpretationHtml(String(match?.interpretation || '').trim());
      }
      const fmt = data.labFormat === 'narrative' ? 'narrative' : 'parameter';
      setLabFormat(fmt);

      // Collapse rows that share a parameter name. A lab saved more than
      // once used to store a full extra copy of every row, so the same
      // section could come back two or more times — the editor must show
      // it once. The surviving row is the first one that actually carries
      // text (a blank duplicate never wins over the real value); the rest
      // are remembered in `staleCodes` and deleted on save.
      //
      // Only narrative labs are collapsed. A numeric panel can legitimately
      // repeat a parameter name (a re-run on the same day, an analyzer row
      // alongside a manual one), and silently deleting one of those would
      // destroy a real result.
      const stale = [];
      const kept  = [];
      if (fmt === 'narrative') {
        const byName = new Map();
        loaded.forEach((r) => {
          const name = String(r.parameterName || r.test_name || '').trim();
          const prev = byName.get(name);
          if (!prev) { byName.set(name, r); kept.push(r); return; }
          // Prefer whichever copy has text; discard the other.
          const prevHasText = String(prev.result ?? '').trim().length > 0;
          const thisHasText = String(r.result ?? '').trim().length > 0;
          if (!prevHasText && thisHasText) {
            byName.set(name, r);
            kept[kept.indexOf(prev)] = r;
            if (prev.trans_code && prev.trans_code !== '-') stale.push(prev.trans_code);
          } else if (r.trans_code && r.trans_code !== '-') {
            stale.push(r.trans_code);
          }
        });
      } else {
        kept.push(...loaded);
      }
      setStaleCodes(stale);

      const toRow = (r, idx) => ({
        __key:         `${r.trans_code || 'x'}-${idx}`,
        parameterName: r.parameterName || r.test_name || '',
        result:        r.result ?? '',
        flag:          (r.flag || 'normal').toLowerCase(),
        unit:          r.unit || '',
        range:         r.range || '',
        trans_code:    r.trans_code || '',
        // Carry the extras through even though we don't edit them —
        // saveLabResults writes them back so we mustn't drop values.
        result_alt:        r.result_alt || '',
        unit_alt:          r.unit_alt || '',
        entered_unit_kind: r.entered_unit_kind || null,
        category:          r.category || row.cat || '',
        isNew:             false,
      });

      // ── Show every parameter the lab defines, not only what was saved ──
      // A technician who filled some parameters and missed others (or whose
      // save failed partway) has to be able to come back and enter the rest.
      // So the catalog list drives the order and the saved rows are matched
      // into it; anything with no stored result renders empty and, once
      // typed, is written as a new row against this same date and lab number
      // so it joins the existing report instead of starting a new one.
      const catalog = Array.isArray(data.catalogParameters) ? data.catalogParameters : [];
      const norm = (s) => String(s || '').trim().toLowerCase();

      let merged;
      if (catalog.length === 0) {
        merged = kept.map(toRow);
      } else {
        // Saved rows keyed by name. A parameter lab may legitimately hold
        // more than one row for the same name (a same-day re-run, an
        // analyzer row beside a manual one), so each name keeps a queue and
        // every stored row is shown — we must never hide a real result.
        const savedByName = new Map();
        kept.forEach((r) => {
          const k = norm(r.parameterName || r.test_name);
          if (!savedByName.has(k)) savedByName.set(k, []);
          savedByName.get(k).push(r);
        });

        merged = [];
        catalog.forEach((p, idx) => {
          const k = norm(p.test);
          const matches = savedByName.get(k);
          if (matches && matches.length > 0) {
            matches.forEach((m, j) => merged.push(toRow(m, `${idx}-${j}`)));
            savedByName.delete(k);
          } else {
            // Never entered — a blank row the technician can fill in now.
            merged.push({
              __key:         `new-${idx}`,
              parameterName: p.test,
              result:        '',
              flag:          'normal',
              unit:          p.unit || '',
              range:         rangeForPatient(p, row),
              trans_code:    '',
              result_alt:        '',
              unit_alt:          '',
              entered_unit_kind: null,
              category:          p.cat || row.cat || '',
              isNew:             true,
            });
          }
        });

        // Stored rows whose parameter is no longer in the catalog (renamed
        // or deleted since). They still hold a real result, so they belong
        // on screen — appended after the catalogue-ordered rows.
        let extra = 0;
        savedByName.forEach((list) => {
          list.forEach((r) => merged.push(toRow(r, `x${extra++}`)));
        });
      }

      setRows(merged);
      setImpression(String(data.impression || '').trim());
      setGeneralComment(String(data.generalComment || '').trim());
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load results.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.id, row?.cat, isoDate, row?.path_no]);

  const updateRow = (key, patch) => {
    setRows((prev) => prev.map((r) => (r.__key === key ? { ...r, ...patch } : r)));
  };

  const isNarrative = labFormat === 'narrative';

  // What actually gets written. A parameter that was never entered and is
  // still blank is left alone — sending it would store an empty result and
  // put a meaningless row on the report. Fill it in and it saves like any
  // other; the backend has no matching stored row, so it writes a new one
  // against this lab's date and number and it joins the same report.
  const rowsToSave = rows.filter((r) => !(r.isNew && String(r.result ?? '').trim() === ''));
  const newlyEntered = rowsToSave.filter((r) => r.isNew).length;
  const pendingBlank = rows.length - rowsToSave.length;

  const handleSave = async () => {
    if (rowsToSave.length === 0) {
      await Swal.fire({
        icon: 'info',
        title: 'Nothing to save',
        text: rows.length === 0
          ? (isNarrative
              ? 'There are no sections loaded for this lab.'
              : 'There are no parameter rows loaded for this lab.')
          : `Nothing has been entered yet. Type a result into at least one ${isNarrative ? 'section' : 'parameter'} before saving.`,
      });
      return;
    }

    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Overwrite saved result?',
      html: `
        <div style="text-align:left; font-size:0.92rem; line-height:1.55">
          You are editing the <strong>${row?.cat || 'lab'}</strong> saved on
          <strong>${isoDate || 'unknown date'}</strong> for
          <strong>${row?.name || 'this patient'}</strong>.<br/><br/>
          Saving will replace the stored values. The original recorder's
          name will be overwritten with <strong>${currentUser || 'the current user'}</strong>.
          ${newlyEntered > 0 ? `<br/><br/>
          <strong>${newlyEntered} ${isNarrative ? 'section' : 'parameter'}${newlyEntered === 1 ? '' : 's'}</strong>
          you have just filled in will be added to this same lab, so ${newlyEntered === 1 ? 'it appears' : 'they appear'}
          on the ${isoDate || 'existing'} report.` : ''}
          ${pendingBlank > 0 ? `<br/><br/>
          ${pendingBlank} still-empty ${isNarrative ? 'section' : 'parameter'}${pendingBlank === 1 ? '' : 's'}
          will be left out — you can come back and add ${pendingBlank === 1 ? 'it' : 'them'} any time.` : ''}
          ${staleCodes.length > 0 ? `<br/><br/>
          It will also remove <strong>${staleCodes.length} duplicate row${staleCodes.length === 1 ? '' : 's'}</strong>
          left behind by an earlier double-save, so the report stops printing
          the same ${isNarrative ? 'section' : 'parameter'} more than once.` : ''}
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Yes, save',
      cancelButtonText:  'Cancel',
      confirmButtonColor: '#dc2626',
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const payload = {
        patientId:    row.id,
        testName:     row.cat,            // saveLabResults' `testName` is the category
        testCategory: row.cat,
        lab_no:       row.path_no,
        results:      rowsToSave.map((r) => ({
          parameterName:     r.parameterName,
          result:            r.result,
          flag:              r.flag,
          unit:              r.unit,
          range:             r.range,
          // Present on stored rows → the backend UPDATEs. Blank on a
          // parameter being entered for the first time → it INSERTs, using
          // the enteredDate and lab_no below so the new row lands in this
          // same lab rather than creating a separate one.
          trans_code:        r.trans_code,
          result_alt:        r.result_alt,
          unit_alt:          r.unit_alt,
          entered_unit_kind: r.entered_unit_kind,
          category:          r.category,
        })),
        enteredBy:      currentUser || '',
        enteredDate:    isoDate,
        patientInfo:    {
          name:   row.name || '',
          age:    row.age  || '',
          gender: row.gender || '',
        },
        generalComment,
        impression,
      };
      const { data } = await axios.post(saveLabResults, payload, { timeout: 30000 });
      if (data?.success !== false) {
        // Retire the duplicate rows only after the surviving ones have been
        // written — if the save had failed we'd have deleted the copies for
        // nothing. Failures here are reported but don't undo the save; the
        // extra rows simply stay until the next edit or migration 044.
        let removed = 0;
        let removeFailed = false;
        for (const code of staleCodes) {
          try {
            await axios.delete(deleteResultC(code), { timeout: 15000 });
            removed += 1;
          } catch (_) { removeFailed = true; }
        }
        setStaleCodes([]);

        await Swal.fire({
          icon: removeFailed ? 'warning' : 'success',
          title: 'Saved',
          text: removeFailed
            ? 'The edited results were written back, but some duplicate rows could not be removed.'
            : removed > 0
              ? `The edited results have been written back and ${removed} duplicate row${removed === 1 ? '' : 's'} removed.`
              : 'The edited results have been written back.',
          timer: removeFailed ? undefined : 2400,
          showConfirmButton: removeFailed,
        });
        if (typeof onSaved === 'function') onSaved();
        onClose();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Save failed',
          text:  data?.message || 'The server refused the update.',
        });
      }
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Save failed',
        text:  err.response?.data?.message || err.message || 'Could not reach the server.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, height: '92vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.25 }}>
        <HistoryIcon sx={{ color: '#7c3aed' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
            Edit historical lab
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row?.cat || '—'} · {isoDate || '—'} · {row?.name || '—'} · Lab №{row?.path_no || '—'}
          </Typography>
        </Box>
        <Tooltip title="Reload from server">
          <span>
            <IconButton onClick={load} disabled={loading || saving} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton onClick={onClose} disabled={saving} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'action.hover' }}>
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          You're editing a saved result. Saving will overwrite the stored values and
          replace the original recorder's name with <strong>{currentUser || 'the current user'}</strong>.
          Correct only what needs correcting.
        </Alert>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : rows.length === 0 ? (
          <Alert severity="info">
            No {isNarrative ? 'sections were' : 'parameters were'} returned for this lab. The row may have been reversed or the underlying results deleted.
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            {/* Narrative labs were recorded as descriptions, so they're
                edited as descriptions: the section title as a heading and
                the technician's paragraph in a full-width text area. No
                flag, unit or reference range — a description has none, and
                rendering those boxes is what made a narrative lab come back
                looking like a numeric panel. */}
            {isNarrative && (
              <Alert severity="info" icon={<NotesIcon />} sx={{ mb: 0.5 }}>
                This is a <strong>narrative</strong> lab — {rows.length} section{rows.length === 1 ? '' : 's'} of
                descriptive text plus one clinical Impression. Edit the wording directly;
                the report prints it exactly as typed.
                {staleCodes.length > 0 && (
                  <> <strong>{staleCodes.length} duplicate row{staleCodes.length === 1 ? '' : 's'}</strong> from
                  an earlier double-save {staleCodes.length === 1 ? 'was' : 'were'} collapsed here and will be
                  removed when you save.</>
                )}
              </Alert>
            )}

            {/* Every parameter the lab defines is listed, so one that was
                missed at entry can be filled in now and joins this same
                report instead of needing a new lab. */}
            {pendingBlank > 0 && (
              <Alert severity="warning" sx={{ mb: 0.5 }}>
                <strong>{pendingBlank}</strong> of {rows.length} {isNarrative ? 'section' : 'parameter'}
                {rows.length === 1 ? '' : 's'} {pendingBlank === 1 ? 'has' : 'have'} no result yet.
                Type into {pendingBlank === 1 ? 'it' : 'them'} to add {pendingBlank === 1 ? 'it' : 'them'} to
                the {isoDate || 'existing'} report; leave {pendingBlank === 1 ? 'it' : 'them'} blank and
                nothing is written.
              </Alert>
            )}

            {rows.map((r, idx) => (
              <Paper
                key={r.__key}
                elevation={0}
                sx={{
                  p: 1.75, border: '1px solid', borderRadius: 2,
                  // A parameter with nothing entered is outlined so it reads
                  // as an invitation to fill it in, not as a saved value.
                  borderColor: r.isNew ? 'warning.main' : 'divider',
                  borderStyle: r.isNew ? 'dashed' : 'solid',
                  bgcolor: 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip size="small" label={`#${idx + 1}`} sx={{ fontWeight: 700 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                    {r.parameterName || (isNarrative ? '(untitled section)' : '(unnamed parameter)')}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {r.trans_code ? (
                    <Chip size="small" variant="outlined" label={`ID ${r.trans_code}`} sx={{ fontFamily: 'monospace' }} />
                  ) : String(r.result ?? '').trim() ? (
                    <Chip size="small" color="success" label="will be added" />
                  ) : (
                    <Chip size="small" color="warning" variant="outlined" label="not entered" />
                  )}
                </Box>

                {isNarrative ? (
                  <TextField
                    label="Findings"
                    value={r.result}
                    onChange={(e) => updateRow(r.__key, { result: e.target.value })}
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={14}
                    placeholder={`Describe findings for ${r.parameterName || 'this section'}…`}
                  />
                ) : (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' },
                    gap: 1.5,
                  }}>
                    <TextField
                      size="small"
                      label="Result"
                      value={r.result}
                      onChange={(e) => updateRow(r.__key, { result: e.target.value })}
                      fullWidth
                      multiline
                      maxRows={6}
                    />
                    <FormControl size="small" fullWidth>
                      <InputLabel>Flag</InputLabel>
                      <Select
                        label="Flag"
                        value={FLAG_OPTIONS.some(f => f.value === r.flag) ? r.flag : 'normal'}
                        onChange={(e) => updateRow(r.__key, { flag: e.target.value })}
                      >
                        {FLAG_OPTIONS.map((f) => (
                          <MenuItem key={f.value} value={f.value} sx={{ color: f.color, fontWeight: 600 }}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Unit"
                      value={r.unit}
                      InputProps={{ readOnly: true }}
                      fullWidth
                      sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
                    />
                    <TextField
                      size="small"
                      label="Ref. Range"
                      value={r.range}
                      InputProps={{ readOnly: true }}
                      fullWidth
                      sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
                    />
                  </Box>
                )}
              </Paper>
            ))}

            <Divider sx={{ my: 1 }} />

            {/* One clinical Impression is stored per narrative lab session
                and prints as the conclusion line under the report. Hidden
                for parameter labs, which have no such field. */}
            {isNarrative && (
              <TextField
                label="Impression"
                multiline
                minRows={3}
                maxRows={8}
                value={impression}
                onChange={(e) => setImpression(e.target.value)}
                placeholder="Clinical conclusion — e.g. Findings are consistent with Chronic Myeloid Leukemia."
                fullWidth
              />
            )}
            <TextField
              label="General comment"
              multiline
              minRows={2}
              maxRows={5}
              value={generalComment}
              onChange={(e) => setGeneralComment(e.target.value)}
              fullWidth
            />

            {/* Interpretation / reference table — the same block that
                prints under the report. Pulled from lab_cat for this
                category so what the editor sees matches what the
                doctor will see on the printed report. Read-only: this
                is category-level (shared across every patient's lab
                of the same category), so editing it belongs in
                Setup Lab, not here. */}
            {interpretationHtml && (
              <Paper
                elevation={0}
                sx={{
                  mt: 1,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Chip
                    size="small"
                    label="Interpretation / Reference Table"
                    sx={{ fontWeight: 700, bgcolor: 'rgba(124,58,237,0.10)', color: '#7c3aed' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Prints under the report. Managed in Setup Lab.
                  </Typography>
                </Box>
                {/* Renders the stored HTML as-is. The block is authored
                    by admins in the Setup Lab interpretation editor and
                    already sanitized at input time — same call the
                    report template uses to render it. */}
                <Box
                  sx={{
                    '& table': { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
                    '& th, & td': { border: '1px solid #d9dee5', padding: '6px 10px', verticalAlign: 'top' },
                    '& th': { bgcolor: 'rgba(15,23,42,0.04)', fontWeight: 700 },
                    '& p':  { margin: '4px 0' },
                    '& ul, & ol': { paddingLeft: '20px', margin: '4px 0' },
                    color: 'text.primary',
                  }}
                  dangerouslySetInnerHTML={{ __html: interpretationHtml }}
                />
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {rows.length > 0
            ? `${rowsToSave.length} of ${rows.length} ${isNarrative ? 'section' : 'parameter'}${rows.length === 1 ? '' : 's'} will be saved`
              + (newlyEntered > 0 ? ` · ${newlyEntered} newly entered` : '')
              + (pendingBlank > 0 ? ` · ${pendingBlank} still empty` : '')
              + (staleCodes.length > 0 ? ` · ${staleCodes.length} duplicate${staleCodes.length === 1 ? '' : 's'} to remove` : '')
              + ` · original recorder: ${row?.test_by || 'unknown'}`
            : ''}
        </Typography>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="warning"
          disabled={saving || loading || rowsToSave.length === 0}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
